import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Sparkles, CheckCircle2, Camera, Check, Send, RotateCcw, ChevronDown,
  Loader2, Play, Pause, Music, Lightbulb, Clapperboard, Zap, Flame, X,
  Palette, Copy, Download, ExternalLink, Radio, KanbanSquare, FolderOpen, Search, BarChart3, History,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useAccount } from "@/context/AccountContext";
import { StatusBadge, FlagBadges } from "@/components/Badges";
import { FileUpload, resolveMediaUrl } from "@/components/FileUpload";
import { BulkDownloadBar } from "@/pages/BrandHub";
import { uploadFileChunked } from "@/lib/chunkUpload";
import { EventsTab, PipelineTab } from "@/components/CompanionTabs";
import { DateienPanel } from "@/pages/Dateien";
import Performance from "@/pages/Performance";
import PostingHistory from "@/pages/PostingHistory";
import { TYPE_LABEL } from "@/lib/constants";

const TABS = [
  { key: "ideas", label: "Ideen", icon: Lightbulb, color: "#FF9500" },
  { key: "events", label: "Events", icon: Radio, color: "#FF2D55" },
  { key: "pipeline", label: "Pipeline", icon: KanbanSquare, color: "#AF52DE" },
  { key: "capture", label: "Vor Ort", icon: Camera, color: "#5AC8FA" },
  { key: "files", label: "Dateien", icon: FolderOpen, color: "#FFCC00" },
  { key: "brand", label: "Brand", icon: Palette, color: "#FF3B30" },
  { key: "approvals", label: "Freigaben", icon: CheckCircle2, color: "#32ADE6" },
  { key: "upload", label: "Upload", icon: Send, color: "#007AFF" },
  { key: "performance", label: "Performance", icon: BarChart3, adminOnly: true, color: "#00C7BE" },
  { key: "history", label: "Posting-History", icon: History, adminOnly: true, color: "#8E8E93" },
];

export default function MobileCompanion() {
  const { user } = useAuth();
  const { currentKey } = useAccount();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get("tab");
  const [tab, setTab] = useState(() => TABS.some((item) => item.key === requestedTab) ? requestedTab : "capture");
  const [prefill, setPrefill] = useState("");

  useEffect(() => {
    setTab(TABS.some((item) => item.key === requestedTab) ? requestedTab : "capture");
  }, [requestedTab]);

  if (!currentKey) return <div className="min-h-screen flex items-center justify-center text-neutral-400">Lade…</div>;

  const selectTab = (nextTab) => {
    setTab(nextTab);
    setSearchParams({ tab: nextTab }, { replace: true });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const goCaptureWith = (title) => { setPrefill(title); selectTab("capture"); };
  const activeTab = TABS.find((item) => item.key === tab) || TABS.find((item) => item.key === "pipeline");

  return (
    <div className="mobile-companion-page min-h-full w-full max-w-[560px] mx-auto text-slate-950 flex flex-col" data-testid="mobile-companion-page">
      <main className="mobile-liquid-main flex-1 px-3.5 pt-5 pb-8">
        <section className="mobile-liquid-page-intro mb-5" data-testid="companion-page-intro">
          <div className="mobile-liquid-page-icon" style={{ background: activeTab?.color }}>
            {activeTab && <activeTab.icon className="h-5 w-5" />}
          </div>
          <div className="min-w-0">
            <p className="text-[10px] font-extrabold uppercase tracking-[0.16em] text-slate-500">Mobile Redaktion</p>
            <h1 className="text-[26px] leading-[1.05] font-extrabold tracking-[-0.04em] text-slate-950 mt-1">{activeTab?.label}</h1>
          </div>
        </section>
        {tab === "ideas" && <IdeasTab accountKey={currentKey} onShoot={goCaptureWith} />}
        {tab === "events" && <EventsTab accountKey={currentKey} />}
        {tab === "pipeline" && <PipelineTab accountKey={currentKey} />}
        {tab === "capture" && <CaptureTab accountKey={currentKey} user={user} prefill={prefill} onDone={() => { setPrefill(""); selectTab("approvals"); }} />}
        {tab === "files" && <DateienPanel />}
        {tab === "brand" && <BrandTab accountKey={currentKey} />}
        {tab === "approvals" && <ApprovalsTab accountKey={currentKey} />}
        {tab === "upload" && <UploadTab accountKey={currentKey} />}
        {tab === "performance" && <Performance accountKey={currentKey} />}
        {tab === "history" && <PostingHistory accountKey={currentKey} />}
      </main>
    </div>
  );
}

function IdeasTab({ accountKey, onShoot }) {
  const [topics, setTopics] = useState([]);
  useEffect(() => { api.get("/topics", { params: { account: accountKey } }).then(({ data }) => setTopics(data)); }, [accountKey]);
  return (
    <div className="space-y-3">
      <CurateCard accountKey={accountKey} />
      <h2 className="font-head font-bold text-lg">Was sollen wir drehen?</h2>
      <p className="text-sm text-neutral-400">Datenbasierte Themen mit Erfolgs-Score. Tippe „Aufnehmen", um direkt vor Ort zu erfassen.</p>
      {topics.length === 0 && <p className="text-neutral-500 text-sm py-8 text-center">Noch keine Themen.</p>}
      {topics.map((t) => (
        <div key={t.id} data-testid={`companion-idea-${t.id}`} className="bg-white border border-neutral-200 rounded-2xl shadow-sm p-4">
          <div className="flex items-start justify-between gap-2">
            <h3 className="font-head font-semibold text-[15px]">{t.title}</h3>
            {typeof t.virality_score === "number" && (
              <span className={`shrink-0 inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full ${t.virality_score >= 70 ? "bg-orange-500/20 text-orange-700" : t.virality_score >= 40 ? "bg-amber-500/20 text-amber-300" : "bg-neutral-700 text-neutral-600"}`}>
                <Zap className="h-3 w-3" /> {t.virality_score}
              </span>
            )}
          </div>
          {t.summary && <p className="text-sm text-neutral-400 mt-1">{t.summary}</p>}
          <div className="flex items-center gap-2 mt-3">
            {t.heat === "hot" && <span className="inline-flex items-center gap-1 text-[11px] text-orange-300"><Flame className="h-3.5 w-3.5" /> heiß</span>}
            <button data-testid={`companion-shoot-${t.id}`} onClick={() => onShoot(t.title)}
              className="ml-auto inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg bg-orange-500 text-white">
              <Camera className="h-3.5 w-3.5" /> Aufnehmen
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

function CurateCard({ accountKey }) {
  const [open, setOpen] = useState(false);
  const [link, setLink] = useState("");
  const [handle, setHandle] = useState("");
  const [note, setNote] = useState("");
  const [caption, setCaption] = useState("");
  const [media, setMedia] = useState(null);
  const [saving, setSaving] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [autoPreview, setAutoPreview] = useState(false);
  const previewedFor = useRef("");

  useEffect(() => {
    const l = link.trim();
    if (!/instagram\.com\/(p|reel|reels|tv)\//.test(l) || l === previewedFor.current) return;
    if (media && !autoPreview) return;
    const t = setTimeout(async () => {
      previewedFor.current = l;
      setPreviewBusy(true);
      try {
        const { data } = await api.get("/meta/preview-link", { params: { url: l } });
        setMedia({ kind: "image", url: data.media_url });
        setAutoPreview(true);
        if (data.author) setHandle((h) => h || data.author);
        toast.success("Instagram-Vorschau geladen ✨");
      } catch {
        toast.info("Keine Vorschau verfügbar — Screenshot hochladen");
      } finally {
        setPreviewBusy(false);
      }
    }, 700);
    return () => clearTimeout(t);
  }, [link]); // eslint-disable-line react-hooks/exhaustive-deps

  const generateCaption = async () => {
    setAiBusy(true);
    try {
      const { data } = await api.post("/ai/repost-caption", { account_key: accountKey, handle, note });
      setCaption(data.caption || "");
      toast.success("KI-Caption eingefügt ✨");
    } catch (e) { toast.error(e.response?.data?.detail || "KI-Caption fehlgeschlagen"); } finally { setAiBusy(false); }
  };

  const submit = async () => {
    if (!link.trim() && !media) return toast.error("Link einfügen oder Screenshot hochladen");
    const h = handle.trim().replace(/^@/, "");
    setSaving(true);
    try {
      await api.post("/content", {
        account_key: accountKey,
        title: h ? `Repost: @${h}` : "Kuratierter Beitrag",
        content_type: "story",
        status: "idea",
        caption: caption.trim() || [h ? `Repost von @${h}` : "", note.trim()].filter(Boolean).join(" — "),
        source_link: link.trim(),
        media_url: media?.kind === "image" ? media.url : "",
      });
      toast.success("Als Idee übernommen ✨");
      setLink(""); setHandle(""); setNote(""); setCaption(""); setMedia(null); setAutoPreview(false); previewedFor.current = ""; setOpen(false);
    } catch { toast.error("Übernehmen fehlgeschlagen"); } finally { setSaving(false); }
  };

  if (!open) {
    return (
      <button data-testid="companion-curate-open" onClick={() => setOpen(true)}
        className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border border-orange-300 bg-orange-500/10 text-orange-700 text-sm font-semibold">
        <Sparkles className="h-4 w-4" /> Beitrag kuratieren (Verein/Betrieb)
      </button>
    );
  }
  return (
    <div data-testid="companion-curate-card" className="bg-white border border-orange-300 rounded-2xl shadow-sm p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="font-head font-semibold text-[15px] flex items-center gap-1.5"><Sparkles className="h-4 w-4 text-orange-600" /> Kuratieren</h3>
        <button onClick={() => setOpen(false)} className="text-neutral-500"><X className="h-4 w-4" /></button>
      </div>
      <input data-testid="companion-curate-link" value={link} onChange={(e) => setLink(e.target.value)}
        placeholder="Instagram-Link einfügen…" className="w-full bg-neutral-100 border border-neutral-200 rounded-lg px-3 py-2.5 text-sm" />
      {previewBusy && (
        <div data-testid="companion-curate-preview-loading" className="flex items-center gap-1.5 text-[12px] text-orange-600">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Vorschau wird geladen…
        </div>
      )}
      {media?.kind === "image" && (
        <div className="relative rounded-lg overflow-hidden border border-neutral-300">
          <img data-testid="companion-curate-preview-img" src={resolveMediaUrl(media.url)} alt="" className="w-full max-h-44 object-cover" />
          {autoPreview && (
            <span className="absolute top-1.5 left-1.5 bg-orange-600 text-white text-[10px] font-semibold px-1.5 py-0.5 rounded">Auto-Vorschau</span>
          )}
        </div>
      )}
      <div className="grid grid-cols-2 gap-2">
        <input data-testid="companion-curate-handle" value={handle} onChange={(e) => setHandle(e.target.value)}
          placeholder="@verein_xy" className="bg-neutral-100 border border-neutral-200 rounded-lg px-3 py-2.5 text-sm" />
        <FileUpload dark label={media && !autoPreview ? "Screenshot ✓" : "Screenshot"} accept="image/*" onUploaded={(d) => { setMedia(d); setAutoPreview(false); }} />
      </div>
      <input data-testid="companion-curate-note" value={note} onChange={(e) => setNote(e.target.value)}
        placeholder="Notiz (optional)" className="w-full bg-neutral-100 border border-neutral-200 rounded-lg px-3 py-2.5 text-sm" />
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-neutral-500">Repost-Caption</span>
        <button type="button" data-testid="companion-curate-ai-caption" onClick={generateCaption} disabled={aiBusy}
          className="inline-flex items-center gap-1 text-[12px] font-semibold text-orange-600 disabled:opacity-50">
          {aiBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} KI-Caption
        </button>
      </div>
      <textarea data-testid="companion-curate-caption" value={caption} onChange={(e) => setCaption(e.target.value)} rows={3}
        placeholder="Leer lassen für automatischen Credit — oder KI-Vorschlag." className="w-full bg-neutral-100 border border-neutral-200 rounded-lg px-3 py-2.5 text-sm resize-none" />
      <button data-testid="companion-curate-save" onClick={submit} disabled={saving}
        className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg bg-orange-600 text-white font-semibold disabled:opacity-50">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Als Idee übernehmen
      </button>
    </div>
  );
}

function BrandTab({ accountKey }) {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    api.get("/brand/assets", { params: { account: accountKey } })
      .then(({ data }) => {
        setAssets(data);
        const firstWith = ["logo", "color", "font", "video", "preset", "canva_template"].find((c) => data.some((a) => a.category === c));
        if (firstWith) setCat(firstWith);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [accountKey]);

  const copy = async (t, label = "Kopiert") => {
    try { await navigator.clipboard.writeText(t); toast.success(label); }
    catch { toast.error("Kopieren nicht erlaubt"); }
  };
  const groups = [
    ["logo", "Logos"], ["color", "Farben"], ["font", "Schriften"],
    ["video", "Videos"], ["preset", "Presets"], ["canva_template", "Canva"],
  ];
  const [cat, setCat] = useState("logo");
  const [query, setQuery] = useState("");
  const [videoSub, setVideoSub] = useState("all");
  const [selected, setSelected] = useState(new Set());
  const toggleSel = (id) => setSelected((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const countOf = (c) => assets.filter((a) => a.category === c).length;

  if (loading) return <div className="py-16 flex justify-center text-neutral-500"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  const q = query.trim().toLowerCase();
  let items = q
    ? assets.filter((a) => [a.name, a.description, a.hex, a.text_content].some((v) => v && v.toLowerCase().includes(q)))
    : assets.filter((a) => a.category === cat);

  const videoSubs = cat === "video" && !q
    ? [...new Set(items.map((a) => a.subcategory || "Sonstiges"))].sort((a, b) =>
        a === "Sonstiges" ? 1 : b === "Sonstiges" ? -1 : a.localeCompare(b))
    : [];
  if (cat === "video" && !q && videoSub !== "all") {
    items = items.filter((a) => (a.subcategory || "Sonstiges") === videoSub);
  }

  return (
    <div data-testid="companion-brand-tab" className="space-y-4">
      <div>
        <h2 className="font-head font-bold text-lg flex items-center gap-2"><Palette className="h-5 w-5 text-orange-600" /> Brand Hub</h2>
        <p className="text-sm text-neutral-400">CI-Assets des Kanals — immer die gleiche Basis für alle.</p>
      </div>
      <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1" data-testid="companion-brand-cats">
        {groups.map(([key, label]) => (
          <button
            key={key}
            data-testid={`companion-brand-cat-${key}`}
            onClick={() => { setCat(key); setVideoSub("all"); }}
            className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-bold border transition ${
              cat === key ? "bg-neutral-900 text-white border-neutral-900" : "bg-white text-neutral-600 border-neutral-200"
            }`}
          >
            {label}{countOf(key) > 0 && <span className={`ml-1 ${cat === key ? "text-white/60" : "text-neutral-400"}`}>{countOf(key)}</span>}
          </button>
        ))}
      </div>
      {cat === "video" && !q && videoSubs.length > 0 && !(videoSubs.length === 1 && videoSubs[0] === "Sonstiges") && (
        <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1" data-testid="companion-video-subcats">
          <button
            data-testid="companion-video-subcat-all"
            onClick={() => setVideoSub("all")}
            className={`shrink-0 px-2.5 py-1 rounded-full text-[11px] font-bold border transition ${
              videoSub === "all" ? "bg-orange-600 text-white border-orange-600" : "bg-white text-neutral-500 border-neutral-200"
            }`}
          >
            Alle
          </button>
          {videoSubs.map((s) => (
            <button
              key={s}
              data-testid={`companion-video-subcat-${s}`}
              onClick={() => setVideoSub(s)}
              className={`shrink-0 px-2.5 py-1 rounded-full text-[11px] font-bold border transition ${
                videoSub === s ? "bg-orange-600 text-white border-orange-600" : "bg-white text-neutral-500 border-neutral-200"
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      )}
      {items.length === 0 ? (
        <p className="text-neutral-500 text-sm py-8 text-center">Keine Assets in dieser Kategorie.</p>
      ) : cat === "color" ? (
        <div className="grid grid-cols-2 gap-2">
          {items.map((a) => (
            <button key={a.id} data-testid={`companion-brand-color-${a.id}`} onClick={() => copy(a.hex, `${a.hex} kopiert`)}
              className="flex items-center gap-2.5 bg-white border border-neutral-200 rounded-2xl shadow-sm p-2.5 text-left">
              <span className="h-8 w-8 rounded-lg shrink-0 border border-neutral-300" style={{ background: a.hex }} />
              <span className="min-w-0">
                <span className="block text-[13px] font-semibold truncate">{a.name}</span>
                <span className="block text-[11px] font-mono text-neutral-400">{a.hex}</span>
              </span>
            </button>
          ))}
        </div>
      ) : (
        <>
          <BulkDownloadBar accountKey={accountKey} selected={selected} onClear={() => setSelected(new Set())} assets={assets} />
          <div className="grid grid-cols-2 gap-2.5">
          {items.map((a) => (
            <div key={a.id} data-testid={`companion-brand-asset-${a.id}`} className={`relative bg-white border rounded-2xl shadow-sm overflow-hidden flex flex-col ${selected.has(a.id) ? "border-fuchsia-400 ring-2 ring-fuchsia-500/20" : "border-neutral-200"}`}>
              {a.media_url && (
                <input
                  type="checkbox"
                  data-testid={`companion-brand-select-${a.id}`}
                  checked={selected.has(a.id)}
                  onChange={() => toggleSel(a.id)}
                  className="absolute top-2 left-2 z-10 h-5 w-5 accent-fuchsia-600 rounded shadow bg-white/90"
                />
              )}
              {a.file_kind === "image" && a.media_url ? (
                <div className="h-24 bg-neutral-50 border-b border-neutral-100 flex items-center justify-center">
                  <img src={resolveMediaUrl(a.media_url)} alt="" loading="lazy" className="max-h-20 max-w-full object-contain p-1.5" />
                </div>
              ) : a.file_kind === "video" && a.media_url ? (
                <video src={`${resolveMediaUrl(a.media_url)}#t=0.1`} playsInline controls className="h-24 w-full object-cover bg-neutral-900 border-b border-neutral-100" muted preload="metadata" />
              ) : null}
              <div className="p-2.5 flex-1 flex flex-col">
                <p className="text-[13px] font-semibold truncate">{a.name}</p>
                {a.description && <p className="text-[11px] text-neutral-400 truncate">{a.description}</p>}
                <div className="flex items-center gap-1.5 mt-auto pt-2">
                  {a.text_content && (
                    <button onClick={() => copy(a.text_content)} className="h-8 w-8 rounded-lg bg-neutral-100 flex items-center justify-center text-neutral-600">
                      <Copy className="h-3.5 w-3.5" />
                    </button>
                  )}
                  {a.media_url && (
                    <a href={resolveMediaUrl(a.media_url)} target="_blank" rel="noopener noreferrer" download
                      className="h-8 w-8 rounded-lg bg-neutral-100 flex items-center justify-center text-neutral-600">
                      <Download className="h-3.5 w-3.5" />
                    </a>
                  )}
                  {a.canva_url && (
                    <a href={a.canva_url} target="_blank" rel="noopener noreferrer"
                      className="h-8 w-8 rounded-lg bg-orange-600/20 border border-orange-500/30 flex items-center justify-center text-orange-700">
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  )}
                </div>
              </div>
            </div>
          ))}
          </div>
        </>
      )}
    </div>
  );
}

function ApprovalsTab({ accountKey }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState("");
  const load = () => api.get("/content", { params: { account: accountKey, status: "review" } }).then(({ data }) => setItems(data));
  useEffect(() => { load(); }, [accountKey]);
  const act = async (item, action) => {
    setBusy(item.id);
    try {
      if (action === "publish") await api.post(`/content/${item.id}/publish`);
      else await api.patch(`/content/${item.id}`, { status: action === "approve" ? "approved" : "creator_working" });
      toast.success(action === "publish" ? "Veröffentlicht 🎉" : action === "approve" ? "Freigegeben" : "Zurück an Creator");
      load();
    } catch { toast.error("Aktion fehlgeschlagen"); } finally { setBusy(""); }
  };
  return (
    <div className="space-y-4">
      <h2 className="font-head font-bold text-lg">Wartet auf Freigabe ({items.length})</h2>
      {items.length === 0 && <p className="text-neutral-500 text-sm py-8 text-center">Alles freigegeben 🎉</p>}
      {items.map((item) => (
        <div key={item.id} data-testid={`companion-approval-${item.id}`} className="bg-white border border-neutral-200 rounded-2xl shadow-sm overflow-hidden">
          <div className="relative aspect-video bg-black">
            {item.video_url ? <video src={resolveMediaUrl(item.video_url)} controls className="h-full w-full object-contain" />
              : item.media_url ? <img src={resolveMediaUrl(item.media_url)} alt="" className="h-full w-full object-cover opacity-80" />
              : <div className="h-full flex items-center justify-center text-neutral-600"><Play className="h-8 w-8" /></div>}
          </div>
          <div className="p-3.5">
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">{TYPE_LABEL[item.content_type]}</span>
              <StatusBadge status={item.status} />
            </div>
            <h3 className="font-head font-semibold mt-1">{item.title}</h3>
            {item.hook && <p className="text-sm text-neutral-600 mt-1">„{item.hook}"</p>}
            <div className="mt-2"><FlagBadges flags={item.flags} /></div>
            <div className="grid grid-cols-3 gap-2 mt-3">
              <button data-testid={`companion-sendback-${item.id}`} onClick={() => act(item, "sendback")} disabled={busy === item.id}
                className="flex items-center justify-center gap-1 text-xs font-medium py-2 rounded-lg bg-neutral-100 text-amber-300 disabled:opacity-50">
                <RotateCcw className="h-3.5 w-3.5" /> Zurück
              </button>
              <button data-testid={`companion-approve-${item.id}`} onClick={() => act(item, "approve")} disabled={busy === item.id}
                className="flex items-center justify-center gap-1 text-xs font-semibold py-2 rounded-lg bg-white text-neutral-900 disabled:opacity-50">
                <Check className="h-3.5 w-3.5" /> Frei
              </button>
              <button data-testid={`companion-publish-${item.id}`} onClick={() => act(item, "publish")} disabled={busy === item.id}
                className="flex items-center justify-center gap-1 text-xs font-semibold py-2 rounded-lg bg-orange-500 text-white disabled:opacity-50">
                {busy === item.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Live
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function CaptureTab({ accountKey, user, prefill, onDone }) {
  const [title, setTitle] = useState(prefill || "");
  const [hook, setHook] = useState("");
  const [type, setType] = useState("reel");
  const [media, setMedia] = useState(null);
  const [saving, setSaving] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);

  useEffect(() => { if (prefill) setTitle(prefill); }, [prefill]);

  const runAI = async () => {
    if (!title.trim()) return toast.error("Erst einen Titel/Thema eingeben");
    setAiBusy(true);
    try {
      const { data } = await api.post("/ai/suggest", { account_key: accountKey, title, summary: hook, content_type: type });
      setHook(data.hook || hook);
      toast.success(data.data_backed ? "KI-Hook auf Basis echter Daten" : "KI-Hook eingefügt");
    } catch { toast.error("KI-Vorschlag fehlgeschlagen"); } finally { setAiBusy(false); }
  };

  const submit = async () => {
    if (!title.trim()) return toast.error("Titel erforderlich");
    setSaving(true);
    try {
      await api.post("/content", {
        account_key: accountKey, title, content_type: type, status: "review",
        assigned_creator: user?.name || "", hook,
        media_url: media?.kind === "image" ? media.url : "",
        video_url: media?.kind === "video" ? media.url : "",
      });
      toast.success("Vor-Ort-Content gesendet – wartet auf Freigabe");
      setTitle(""); setHook(""); setMedia(null);
      onDone?.();
    } catch { toast.error("Senden fehlgeschlagen"); } finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="companion-capture-tab">
      <section className="mobile-capture-summary">
        <div className="mobile-capture-summary-icon"><Camera className="h-6 w-6" /></div>
        <div>
          <p className="text-[11px] font-extrabold uppercase tracking-[0.14em] text-sky-700">Direkt vor Ort</p>
          <h2 className="mt-1 text-[20px] font-extrabold tracking-[-0.03em] text-slate-950">Moment aufnehmen. Redaktion übernimmt.</h2>
          <p className="mt-1.5 text-[13px] leading-relaxed text-slate-600">Foto oder Video sichern, kurz einordnen und direkt zur Freigabe senden.</p>
        </div>
      </section>
      <div className="mobile-capture-card p-4 space-y-5">
        <div className="mobile-capture-section">
          <div className="mobile-capture-label"><span>1</span><label htmlFor="companion-capture-title">Titel / Thema</label></div>
          <input data-testid="companion-capture-title" value={title} onChange={(e) => setTitle(e.target.value)}
            id="companion-capture-title" placeholder="z. B. Live-Musik im LOKAL" className="mobile-pipeline-field mt-2 w-full h-12 rounded-2xl px-3.5 text-sm" />
        </div>
        <div className="mobile-capture-section">
          <div className="flex items-center justify-between">
            <div className="mobile-capture-label"><span>2</span><label htmlFor="companion-capture-hook">Einstieg / Hook</label></div>
            <button data-testid="companion-capture-ai" onClick={runAI} disabled={aiBusy}
              className="mobile-capture-ai min-h-10 inline-flex items-center gap-1.5 px-3 rounded-[13px] text-xs font-bold text-sky-700 disabled:opacity-50">
              {aiBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} KI-Hook
            </button>
          </div>
          <input data-testid="companion-capture-hook" value={hook} onChange={(e) => setHook(e.target.value)}
            id="companion-capture-hook" placeholder="Starker erster Satz…" className="mobile-pipeline-field mt-2 w-full h-12 rounded-2xl px-3.5 text-sm" />
        </div>
        <div className="mobile-capture-section">
          <div className="mobile-capture-label"><span>3</span><p>Format wählen</p></div>
          <div className="mt-2 grid grid-cols-3 gap-2">
            {["reel", "story", "post"].map((t) => (
              <button key={t} data-testid={`companion-capture-type-${t}`} onClick={() => setType(t)}
                className={`min-h-12 rounded-[15px] text-sm font-bold border transition-all ${type === t ? "bg-sky-600 border-sky-600 text-white shadow-md shadow-sky-500/20" : "bg-white/55 border-white/80 text-slate-600"}`}>
                {TYPE_LABEL[t]}
              </button>
            ))}
          </div>
        </div>
        <div className="mobile-capture-section">
          <div className="mobile-capture-label"><span>4</span><p>Foto oder Video</p></div>
          <div className="mobile-capture-upload mt-2">
            <FileUpload label={media ? "Medien ersetzen" : "Foto / Video aufnehmen"} onUploaded={(d) => setMedia(d)} />
          </div>
          {media && (
            <div className="mt-3 rounded-[18px] overflow-hidden border border-white/80 bg-white/50 shadow-sm">
              {media.kind === "video" ? <video src={resolveMediaUrl(media.url)} controls className="w-full" /> : <img src={resolveMediaUrl(media.url)} alt="" className="w-full" />}
            </div>
          )}
        </div>
        <button data-testid="companion-capture-submit" onClick={submit} disabled={saving}
          className="mobile-capture-submit w-full min-h-14 flex items-center justify-center gap-2.5 rounded-[18px] text-white text-[15px] font-extrabold disabled:opacity-50 active:scale-[0.985] transition">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Zur Freigabe senden
        </button>
      </div>
    </div>
  );
}

function ReelTab({ accountKey, onDone }) {
  const [clips, setClips] = useState([]);
  const [brandVideos, setBrandVideos] = useState([]);
  const [brandSel, setBrandSel] = useState([]);
  const [brandOpen, setBrandOpen] = useState(false);
  const [hook, setHook] = useState("");
  const [voiceText, setVoiceText] = useState("");
  const [title, setTitle] = useState("");
  const [length, setLength] = useState(30);
  const [presets, setPresets] = useState([]);
  const [musicPreset, setMusicPreset] = useState("");
  const [playing, setPlaying] = useState("");
  const [aiHooks, setAiHooks] = useState([]);
  const [hooksBusy, setHooksBusy] = useState(false);
  const [job, setJob] = useState(null);
  const [busy, setBusy] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const jobIdRef = useRef("");
  const abortRef = useRef(false);
  const fileRef = useRef();
  const pollRef = useRef();
  const audioRef = useRef(null);
  useEffect(() => () => { clearTimeout(pollRef.current); if (audioRef.current) audioRef.current.pause(); }, []);
  useEffect(() => { api.get("/reels/music-presets").then(({ data }) => setPresets(data.presets || [])).catch(() => {}); }, []);
  useEffect(() => {
    if (!accountKey) return;
    api.get("/brand/assets", { params: { account: accountKey } })
      .then(({ data }) => setBrandVideos(data.filter((a) => a.category === "video" && a.media_url)))
      .catch(() => {});
  }, [accountKey]);

  const toggleBrandClip = (id) => {
    setBrandSel((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (clips.length + prev.length >= 5) { toast.error("Maximal 5 Clips pro Reel"); return prev; }
      return [...prev, id];
    });
  };

  const togglePreview = (p) => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    if (playing === p.key) { setPlaying(""); return; }
    const a = new Audio(resolveMediaUrl(p.preview_url));
    a.volume = 0.7; a.play().catch(() => {}); a.onended = () => setPlaying("");
    audioRef.current = a; setPlaying(p.key);
  };

  const fetchHooks = async () => {
    if (!title.trim()) return toast.error("Bitte zuerst einen Titel eingeben");
    setHooksBusy(true);
    try {
      const { data } = await api.post("/ai/hooks", { account_key: accountKey, title, content_type: "reel" });
      setAiHooks(data.hooks || []);
      if (!data.hooks?.length) toast.error("Keine Vorschläge erhalten");
    } catch { toast.error("KI-Hooks fehlgeschlagen"); } finally { setHooksBusy(false); }
  };

  const submit = async () => {
    if (clips.length + brandSel.length === 0) return toast.error("Bitte mindestens einen Clip aufnehmen oder aus dem Brand Hub wählen");
    setBusy(true);
    abortRef.current = false;
    if (audioRef.current) { audioRef.current.pause(); setPlaying(""); }
    try {
      const clipIds = [];
      for (let i = 0; i < clips.length; i++) {
        setJob({ status: "processing", stage: `Clip ${i + 1}/${clips.length} wird hochgeladen… 0 %` });
        const res = await uploadFileChunked(clips[i], {
          store: "local",
          isAborted: () => abortRef.current,
          onProgress: (p) => setJob({ status: "processing", stage: `Clip ${i + 1}/${clips.length} wird hochgeladen… ${p} %` }),
        });
        clipIds.push(res.upload_id);
      }
      setJob({ status: "processing", stage: "Reel wird erstellt…" });
      const { data } = await api.post("/reels/auto-start", {
        account_key: accountKey,
        target_length: length,
        hook_text: hook,
        attach_title: title || "KI-Auto-Reel",
        music_preset: musicPreset || "",
        clip_uploads: clipIds,
        brand_clip_ids: brandSel,
        voiceover_text: voiceText,
        voiceover_voice: "onyx",
      });
      jobIdRef.current = data.job_id;
      const poll = async () => {
        try {
          const { data: j } = await api.get(`/reels/jobs/${data.job_id}`);
          setJob(j);
          if (j.status === "done") { toast.success("Reel fertig – im Freigabe-Tab!"); setBusy(false); jobIdRef.current = ""; onDone?.(); }
          else if (j.status === "error") { toast.error(j.error || "Reel fehlgeschlagen"); setBusy(false); jobIdRef.current = ""; }
          else if (j.status === "cancelled") { setBusy(false); setJob(null); jobIdRef.current = ""; }
          else { pollRef.current = setTimeout(poll, 3500); }
        } catch {
          pollRef.current = setTimeout(poll, 5000);
        }
      };
      pollRef.current = setTimeout(poll, 3500);
    } catch (e) { if (e.message !== "upload_aborted") toast.error(e.response?.data?.detail || "Upload fehlgeschlagen — bitte erneut versuchen"); setBusy(false); setJob(null); }
  };

  const cancelJob = async () => {
    setCancelling(true);
    abortRef.current = true;
    clearTimeout(pollRef.current);
    try { if (jobIdRef.current) await api.post(`/reels/jobs/${jobIdRef.current}/cancel`); } catch {}
    jobIdRef.current = "";
    setBusy(false);
    setJob(null);
    setCancelling(false);
    toast.info("Reel-Erstellung abgebrochen");
  };

  return (
    <div className="space-y-4">
      <h2 className="font-head font-bold text-lg">KI-Auto-Reel</h2>
      <p className="text-sm text-neutral-400">Mehrere kurze Clips aufnehmen – die KI schneidet ein Hochformat-Reel mit Untertiteln.</p>
      {busy ? (
        <div className="bg-white border border-neutral-200 rounded-2xl shadow-sm p-8 text-center">
          <Loader2 className="h-7 w-7 text-orange-600 animate-spin mx-auto" />
          <p className="text-neutral-700 mt-3 text-sm">{job?.stage || "Reel wird erstellt…"}</p>
          <button data-testid="companion-reel-cancel" onClick={cancelJob} disabled={cancelling}
            className="mt-5 w-full flex items-center justify-center gap-2 py-3 rounded-xl border border-neutral-300 text-neutral-600 text-sm font-semibold active:scale-[0.98] transition disabled:opacity-50">
            <X className="h-4 w-4" /> Abbrechen
          </button>
        </div>
      ) : (
        <div className="bg-white border border-neutral-200 rounded-2xl shadow-sm p-4 space-y-3">
          <button data-testid="companion-reel-add" onClick={() => fileRef.current?.click()}
            className="w-full py-6 rounded-lg border-2 border-dashed border-neutral-300 text-neutral-600 text-sm">
            <Clapperboard className="h-6 w-6 mx-auto mb-1 text-orange-600" /> Clips aufnehmen / wählen ({clips.length}/5)
          </button>
          <input ref={fileRef} type="file" accept="video/*" multiple hidden
            onChange={(e) => setClips((p) => [...p, ...Array.from(e.target.files || [])].slice(0, Math.max(0, 5 - brandSel.length)))} />
          {clips.length > 0 && (
            <div className="space-y-1.5">
              {clips.map((c, i) => (
                <div key={i} className="flex items-center gap-2 bg-neutral-100 rounded-lg px-3 py-2 text-xs">
                  <span className="truncate flex-1">{c.name}</span>
                  <button onClick={() => setClips((p) => p.filter((_, x) => x !== i))} className="text-neutral-400"><X className="h-3.5 w-3.5" /></button>
                </div>
              ))}
            </div>
          )}
          {brandVideos.length > 0 && (
            <div data-testid="companion-reel-brand">
              <button type="button" data-testid="companion-reel-brand-toggle" onClick={() => setBrandOpen((o) => !o)}
                className={`w-full flex items-center justify-between gap-2 rounded-lg border px-3 py-2.5 text-sm font-semibold ${brandSel.length ? "border-violet-400 bg-violet-500/10 text-violet-700" : "border-neutral-300 bg-neutral-100 text-neutral-600"}`}>
                <span className="flex items-center gap-2"><Palette className="h-4 w-4" /> B-Roll aus dem Brand Hub</span>
                <span className="text-xs">{brandSel.length ? `${brandSel.length} gewählt` : brandOpen ? "▲" : "▼"}</span>
              </button>
              {brandOpen && (
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {brandVideos.map((a) => {
                    const on = brandSel.includes(a.id);
                    return (
                      <button key={a.id} type="button" data-testid={`companion-reel-brand-clip-${a.id}`}
                        onClick={() => toggleBrandClip(a.id)}
                        className={`relative text-left rounded-lg overflow-hidden border-2 ${on ? "border-violet-500" : "border-neutral-200"}`}>
                        <video src={resolveMediaUrl(a.media_url)} muted preload="metadata" className="h-16 w-full object-cover bg-neutral-900" />
                        {on && <span className="absolute top-1 right-1 h-4 w-4 rounded-full bg-violet-600 text-white text-[10px] flex items-center justify-center">✓</span>}
                        <span className="block px-2 py-1 text-[10px] font-semibold text-neutral-700 truncate bg-white">
                          {a.name}{a.subcategory ? ` · ${a.subcategory}` : ""}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
          <input data-testid="companion-reel-title" value={title} onChange={(e) => setTitle(e.target.value)}
            placeholder="Titel" className="w-full bg-neutral-100 border border-neutral-200 rounded-lg px-3 py-2.5 text-sm" />
          <input data-testid="companion-reel-hook" value={hook} onChange={(e) => setHook(e.target.value)}
            placeholder="Hook-Einblendung (optional)" className="w-full bg-neutral-100 border border-neutral-200 rounded-lg px-3 py-2.5 text-sm" />
          <textarea data-testid="companion-reel-voiceover" value={voiceText} onChange={(e) => setVoiceText(e.target.value)} rows={2}
            placeholder="🎙️ KI-Voiceover-Text (optional) — wird eingesprochen und über das Reel gelegt"
            className="w-full bg-neutral-100 border border-neutral-200 rounded-lg px-3 py-2.5 text-sm resize-none" />
          <button type="button" data-testid="companion-reel-ai-hooks" onClick={fetchHooks} disabled={hooksBusy}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-orange-600 disabled:opacity-50">
            {hooksBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} KI-Hook-Vorschläge
          </button>
          {aiHooks.length > 0 && (
            <div className="flex flex-wrap gap-1.5" data-testid="companion-reel-ai-hooks-list">
              {aiHooks.map((h, i) => (
                <button key={i} type="button" data-testid={`companion-reel-ai-hook-${i}`} onClick={() => setHook(h)}
                  className={`text-xs px-2.5 py-1.5 rounded-full border transition ${hook === h ? "bg-orange-600 border-orange-600 text-white" : "bg-orange-500/10 border-orange-500/40 text-orange-700"}`}>
                  {h}
                </button>
              ))}
            </div>
          )}
          {presets.length > 0 && (
            <div data-testid="companion-reel-music">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-neutral-500 flex items-center gap-1.5 mb-1.5"><Music className="h-3.5 w-3.5" /> Musik (lizenzfrei, optional)</p>
              <div className="grid grid-cols-2 gap-1.5">
                {presets.map((p) => {
                  const active = musicPreset === p.key;
                  return (
                    <div key={p.key} data-testid={`companion-reel-music-${p.key}`}
                      onClick={() => setMusicPreset((prev) => (prev === p.key ? "" : p.key))}
                      className={`flex items-center justify-between gap-1 rounded-lg border px-2.5 py-2 cursor-pointer ${active ? "border-orange-500 bg-orange-500/10" : "border-neutral-300 bg-neutral-100"}`}>
                      <span className="text-xs font-medium truncate">{p.name}</span>
                      <button type="button" onClick={(e) => { e.stopPropagation(); togglePreview(p); }}
                        className={`h-6 w-6 shrink-0 rounded-full flex items-center justify-center ${playing === p.key ? "bg-orange-600 text-white" : "bg-white border border-neutral-300 text-neutral-500"}`}>
                        {playing === p.key ? <Pause className="h-3 w-3" /> : <Play className="h-3 w-3" />}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          <div className="flex gap-2">
            {[15, 30, 60].map((l) => (
              <button key={l} onClick={() => setLength(l)}
                className={`flex-1 py-2 rounded-lg text-sm font-medium border ${length === l ? "bg-orange-600 border-orange-600 text-white" : "bg-neutral-100 border-neutral-300 text-neutral-600"}`}>{l}s</button>
            ))}
          </div>
          <button data-testid="companion-reel-submit" onClick={submit} disabled={clips.length + brandSel.length === 0}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-lg bg-gradient-to-r from-orange-600 to-indigo-600 text-white font-semibold disabled:opacity-50">
            <Sparkles className="h-4 w-4" /> Reel erstellen lassen
          </button>
        </div>
      )}
    </div>
  );
}

function UploadTab({ accountKey }) {
  const [items, setItems] = useState([]);
  const load = () => api.get("/content", { params: { account: accountKey } }).then(({ data }) => setItems(data.filter((i) => i.status !== "published")));
  useEffect(() => { load(); }, [accountKey]);
  const attach = async (item, media) => {
    try {
      await api.patch(`/content/${item.id}`, media.kind === "video" ? { video_url: media.url } : { media_url: media.url });
      toast.success("Medien angehängt");
      load();
    } catch { toast.error("Anhängen fehlgeschlagen"); }
  };
  return (
    <div className="space-y-3">
      <h2 className="font-head font-bold text-lg">Medien zu Inhalten hochladen</h2>
      {items.map((item) => (
        <div key={item.id} data-testid={`companion-upload-${item.id}`} className="bg-white border border-neutral-200 rounded-2xl shadow-sm p-3.5 flex items-center gap-3">
          <div className="h-12 w-10 rounded bg-neutral-100 overflow-hidden shrink-0">
            {item.media_url && <img src={resolveMediaUrl(item.media_url)} alt="" className="h-full w-full object-cover" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium truncate">{item.title}</div>
            <div className="text-[11px] text-neutral-500">{TYPE_LABEL[item.content_type]}{item.video_url ? " · Video vorhanden" : ""}</div>
          </div>
          <FileUpload dark label="Upload" onUploaded={(d) => attach(item, d)} />
        </div>
      ))}
      {items.length === 0 && <p className="text-neutral-500 text-sm py-8 text-center">Keine offenen Inhalte.</p>}
    </div>
  );
}
